import { useState, useEffect } from 'react';
import customerService from '../../services/customerService';
import { useLocalization } from '../../contexts/LocalizationContext';
import notificationService from '../../services/notificationService';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';

const BATCH_SIZE = 200;
const MAX_RETRIES = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const PUSH_TYPES = [
  { value: 'announcement', ar: 'إعلان عام (الرئيسية)', en: 'General (open home)' },
  { value: 'home_modal', ar: 'الرئيسية + نافذة لاحقاً', en: 'Home (modal later)' },
  { value: 'product', ar: 'منتج (تفاصيل المنتج)', en: 'Product (open detail)' },
  { value: 'general', ar: 'عام (مثل الإعلان)', en: 'General (same as announcement)' },
];

const AUDIENCES = [
  { value: 'all', ar: 'جميع المستخدمين', en: 'All app users' },
  { value: 'customer', ar: 'عميل محدد (معرّف)', en: 'One customer (ID)' },
];

const NotificationsIndex = () => {
  const { isRtl } = useLocalization();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pushType, setPushType] = useState('announcement');
  const [productId, setProductId] = useState('');
  const [audience, setAudience] = useState('all');
  const [customerId, setCustomerId] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null); // { total, customers, totalCustomers, sent, success, failure, status }

  const t = (ar, en) => (isRtl ? ar : en);

  // Debounced customer lookup; skipped once a customer has been picked.
  useEffect(() => {
    if (audience !== 'customer' || customerId || customerQuery.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const res = await customerService.search(customerQuery.trim());
        if (!cancelled) setSuggestions(res);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(h);
    };
  }, [customerQuery, customerId, audience]);

  const pickCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerQuery(`${c.fullName || t('بدون اسم', 'No name')} — ${c.phone}`);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    if (!title.trim() || !body.trim()) {
      setError(t('عنوان ونص الإشعار مطلوبان', 'Title and body are required'));
      return;
    }
    if (pushType === 'product' && !productId.trim()) {
      setError(t('معرّف المنتج مطلوب', 'Product ID is required'));
      return;
    }
    if (audience === 'customer' && !customerId.trim()) {
      setError(t('اختر عميلاً من القائمة', 'Select a customer from the suggestions'));
      return;
    }
    setLoading(true);
    const payload = {
      title: title.trim(),
      body: body.trim(),
      pushType,
      productId: pushType === 'product' ? productId.trim() : null,
      audience,
      customerId: audience === 'customer' ? customerId.trim() : null,
    };
    const errText = (err) => {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        t('فشل الإرسال', 'Send failed');
      return typeof msg === 'string' ? msg : JSON.stringify(msg);
    };
    try {
      const { totalCustomers, totalTokens } = await notificationService.sendPrepare(payload);
      const state = {
        total: totalTokens,
        totalCustomers,
        customers: 0,
        sent: 0,
        success: 0,
        failure: 0,
        status: 'running',
      };
      setProgress({ ...state });

      let offset = 0;
      while (offset < totalCustomers) {
        let res = null;
        for (let attempt = 1; ; attempt++) {
          try {
            res = await notificationService.sendBatch(payload, offset, BATCH_SIZE);
            break;
          } catch (err) {
            // Retry only transient failures (network / 5xx); validation errors fail immediately.
            const status = err.response?.status;
            const transient = !status || status >= 500;
            if (!transient || attempt >= MAX_RETRIES) throw err;
            await sleep(1500 * attempt);
          }
        }
        if (!res.processedCustomers) break;
        offset += res.processedCustomers;
        state.customers += res.processedCustomers;
        state.sent += res.tokenCount;
        state.success += res.successCount;
        state.failure += res.failureCount;
        setProgress({ ...state });
      }
      state.status = 'done';
      setProgress({ ...state });
      setMessage(
        t(
          `تم الإرسال: ${state.success} نجاح، ${state.failure} فشل`,
          `Sent: ${state.success} succeeded, ${state.failure} failed`,
        ),
      );
    } catch (err) {
      setProgress((p) => (p ? { ...p, status: 'error', errorText: errText(err) } : null));
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  };

  const closeProgress = () => {
    if (progress?.status !== 'running') setProgress(null);
  };

  const pct = progress && progress.total > 0 ? Math.min(100, Math.round((progress.sent / progress.total) * 100)) : 0;

  return (
    <div className="notifications-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{t('الإشعارات', 'Push Notifications')}</h1>
            <p className="page-description">
              {t(
                'إرسال إشعارات فورية لمستخدمي التطبيق المحمول عبر الخادم',
                'Send push notifications to mobile users through the API',
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 'var(--sp-8)' }}>
          <form onSubmit={handleSubmit} className="form-grid" style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
            <Input
              label={t('عنوان الإشعار', 'Notification title')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('مثال: عرض جديد', 'e.g. New offer')}
              required
            />
            <div className="form-group">
              <label className="form-label">{t('نص الإشعار', 'Notification body')} *</label>
              <textarea
                className="form-input"
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t('النص الذي يراه المستخدم', 'Message shown to the user')}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('نوع الإشعار', 'Notification type')}</label>
              <select className="form-input" value={pushType} onChange={(e) => setPushType(e.target.value)}>
                {PUSH_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {isRtl ? o.ar : o.en}
                  </option>
                ))}
              </select>
            </div>

            {pushType === 'product' && (
              <Input
                label={t('معرّف المنتج', 'Product ID')}
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                placeholder="GUID"
                required
              />
            )}

            <div className="form-group">
              <label className="form-label">{t('المستهدفون', 'Audience')}</label>
              <select className="form-input" value={audience} onChange={(e) => setAudience(e.target.value)}>
                {AUDIENCES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {isRtl ? o.ar : o.en}
                  </option>
                ))}
              </select>
            </div>

            {audience === 'customer' && (
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">{t('العميل (الاسم أو الهاتف)', 'Customer (name or phone)')} *</label>
                <input
                  className="form-input"
                  value={customerQuery}
                  onChange={(e) => {
                    setCustomerQuery(e.target.value);
                    setCustomerId('');
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder={t('اكتب الاسم أو رقم الهاتف', 'Type a name or phone number')}
                  autoComplete="off"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul
                    style={{
                      position: 'absolute',
                      top: '100%',
                      insetInline: 0,
                      zIndex: 20,
                      margin: 0,
                      padding: 4,
                      listStyle: 'none',
                      background: '#fffdf8',
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 'var(--r-md)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      maxHeight: 280,
                      overflowY: 'auto',
                    }}
                  >
                    {suggestions.map((c) => (
                      <li
                        key={c.id}
                        onMouseDown={() => pickCustomer(c)}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: 6, display: 'flex', justifyContent: 'space-between', gap: 12 }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span>{c.fullName || t('بدون اسم', 'No name')}</span>
                        <span dir="ltr" style={{ opacity: 0.7 }}>{c.phone}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {error && (
              <div className="error-alert" style={{ margin: 0 }}>
                <span>{error}</span>
              </div>
            )}
            {message && !error && (
              <div className="success-alert" style={{ padding: 'var(--sp-3)', borderRadius: 'var(--r-md)', background: 'rgba(34, 197, 94, 0.12)', color: 'var(--text)' }}>
                {message}
              </div>
            )}

            <div>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? t('جاري الإرسال...', 'Sending...') : t('إرسال الإشعار', 'Send notification')}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Modal
        isOpen={!!progress}
        onClose={closeProgress}
        title={t('جاري إرسال الإشعارات', 'Sending notifications')}
        size="small"
        closeOnEsc={false}
        closeOnOverlay={false}
        showCloseButton={progress?.status !== 'running'}
        footer={
          progress?.status !== 'running' && (
            <Button variant="primary" onClick={closeProgress}>
              {t('إغلاق', 'Close')}
            </Button>
          )
        }
      >
        {progress && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
            <div style={{ height: 14, borderRadius: 999, background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${progress.status === 'done' ? 100 : pct}%`,
                  height: '100%',
                  background: progress.status === 'error' ? '#dc2626' : '#1e3a5f',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 18 }}>
              {progress.status === 'done' ? 100 : pct}%
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <span>{t('تم الإرسال', 'Delivered')}: <b>{progress.sent}</b></span>
              <span>{t('المتبقي', 'Remaining')}: <b>{Math.max(0, progress.total - progress.sent)}</b></span>
              <span>{t('الإجمالي', 'Total')}: <b>{progress.total}</b></span>
            </div>
            <div style={{ fontSize: 13, opacity: 0.75 }}>
              {t('المستخدمون', 'Users')}: {progress.customers} / {progress.totalCustomers}
              {' · '}
              {t('نجاح', 'Success')}: {progress.success}
              {' · '}
              {t('فشل', 'Failed')}: {progress.failure}
            </div>
            {progress.status === 'running' && (
              <div style={{ fontSize: 13 }}>
                {t('الرجاء عدم إغلاق الصفحة حتى يكتمل الإرسال...', 'Please keep this page open until sending completes...')}
              </div>
            )}
            {progress.status === 'done' && (
              <div style={{ color: '#15803d', fontWeight: 600 }}>{t('اكتمل الإرسال لجميع المستخدمين', 'Sending completed for all users')}</div>
            )}
            {progress.status === 'error' && (
              <div style={{ color: '#dc2626' }}>{progress.errorText}</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NotificationsIndex;
